/**
 * Pure tombstone-filter for the DDS fixture tree.
 *
 * DWS / importer pipelines occasionally re-publish a recently-deleted event
 * through the static channel before the source has propagated the delete.
 * useFixtureData keeps a `Set<event_id>` of removed ids and runs every
 * incoming subscription payload through this filter; without it the rows
 * resurrect on the next emit.
 *
 * Narrow shallow-spread the touched paths only — structuredClone(data) on
 * every emit was discarding downstream memo identity for home_team / lineup /
 * staff. Now only the event arrays + their immediate containers get new
 * identity, and if nothing was filtered, the input is returned untouched.
 *
 * @param {Object|null|undefined} data DDS fixture tree
 * @param {Set<string>} removed event_ids to strip
 * @returns same shape; same reference when there is nothing to strip
 */
export const stripTombstonedEvents = (data, removed) => {
  if (!data || !removed || removed.size === 0) return data;
  const filterArr = (arr) => {
    if (!Array.isArray(arr)) return arr;
    const next = arr.filter((e) => !removed.has(e?.event_id));
    return next.length === arr.length ? arr : next;
  };
  let next = data;
  const topEvents = data?.fixture?.[0]?.event;
  const filteredTop = filterArr(topEvents);
  if (filteredTop !== topEvents) {
    next = {
      ...next,
      fixture: [
        { ...next.fixture[0], event: filteredTop },
        ...next.fixture.slice(1),
      ],
    };
  }
  const nestedEvents = data?.season?.[0]?.fixture?.[0]?.event;
  const filteredNested = filterArr(nestedEvents);
  if (filteredNested !== nestedEvents) {
    next = {
      ...next,
      season: [
        {
          ...next.season[0],
          fixture: [
            { ...next.season[0].fixture[0], event: filteredNested },
            ...next.season[0].fixture.slice(1),
          ],
        },
        ...next.season.slice(1),
      ],
    };
  }
  return next;
};
